/**
 * Create Company Modal
 * Includes CID upload, country, corporate_residence fields
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  Upload,
  message,
  Select,
  InputNumber,
  Space,
  Alert,
} from 'antd';
import {
  UploadOutlined,
  IdcardOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import superAdminService, { CompanyCreate, LicenseUploadResponse } from '../../services/superAdminService';
import { prepareDocumentForUpload, formatFileKb } from '../../utils/compressDocument';
import './CreateCompanyModal.css';

const { TextArea } = Input;
const { Option } = Select;

interface CreateCompanyModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  licenseData?: LicenseUploadResponse | null; // Pre-filled data from license upload
}

const CreateCompanyModal: React.FC<CreateCompanyModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  licenseData,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [cidFile, setCidFile] = useState<File | null>(null);
  const [cidFileList, setCidFileList] = useState<UploadFile[]>([]);
  const [licenseFile, setLicenseFile] = useState<File | null>(null);
  const [licenseFileList, setLicenseFileList] = useState<UploadFile[]>([]);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setCidFile(null);
      setCidFileList([]);
      setLicenseFile(null);
      setLicenseFileList([]);
      
      // Pre-fill form if license data is provided - License values have first priority
      if (licenseData?.suggestions) {
        const suggestions = licenseData.suggestions;
        const updates: any = {};
        
        // Set values from license (only if they exist)
        if (suggestions.company_name) {
          updates.name = suggestions.company_name;
        }
        if (suggestions.registration_number) {
          updates.registration_number = suggestions.registration_number;
        }
        if (suggestions.license_type) {
          updates.license_type = suggestions.license_type;
        }
        if (suggestions.tax_id) {
          updates.tax_id = suggestions.tax_id;
        }
        if (suggestions.address) {
          updates.address = suggestions.address;
        }
        if (suggestions.phone) {
          updates.phone = suggestions.phone;
        }
        if (suggestions.email) {
          updates.email = suggestions.email;
        }
        if (suggestions.code) {
          updates.code = suggestions.code;
        }
        
        form.setFieldsValue(updates);
      }
    }
  }, [visible, licenseData, form]);

  const handleCidUpload = async (file: File) => {
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      message.error('CID file must be PDF, JPG, PNG, GIF, or WEBP');
      return false;
    }
    
    let fileToUse: File;
    try {
      fileToUse = await prepareDocumentForUpload(file);
      if (fileToUse.size < file.size) {
        message.success(`CID compressed (${formatFileKb(file.size)} → ${formatFileKb(fileToUse.size)})`);
      }
    } catch (err: any) {
      message.error(err?.message || 'Could not compress CID');
      return false;
    }

    setCidFile(fileToUse);
    setCidFileList([{
      uid: '-1',
      name: fileToUse.name,
      status: 'done',
    }]);
    message.success('CID file selected. Please enter the admin name manually.');
    
    return false; // Prevent auto upload
  };

  const handleLicenseUpload = async (file: File) => {
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      message.error('License file must be PDF, JPG, PNG, GIF, or WEBP');
      return false;
    }
    
    let fileToUse: File;
    try {
      fileToUse = await prepareDocumentForUpload(file);
      if (fileToUse.size < file.size) {
        message.success(`License compressed (${formatFileKb(file.size)} → ${formatFileKb(fileToUse.size)})`);
      }
    } catch (err: any) {
      message.error(err?.message || 'Could not compress license');
      return false;
    }

    setLicenseFile(fileToUse);
    setLicenseFileList([{
      uid: '-1',
      name: fileToUse.name,
      status: 'uploading',
    }]);
    try {
      const response = await superAdminService.uploadLicense(fileToUse);
      setLicenseFileList([{ uid: '-1', name: fileToUse.name, status: 'done' }]);
      const s = response.suggestions || {};
      const updates: any = {};
      if (s.company_name) updates.name = s.company_name;
      if (s.registration_number) updates.registration_number = s.registration_number;
      if (s.license_type) updates.license_type = s.license_type;
      if (s.tax_id) updates.tax_id = s.tax_id;
      if (s.address) updates.address = s.address;
      if (s.phone) updates.phone = s.phone;
      if (s.email) updates.email = s.email;
      if (s.code) updates.code = s.code;
      form.setFieldsValue(updates);
      message.success(response.message || 'License attached.');
    } catch (error: any) {
      setLicenseFileList([{ uid: '-1', name: fileToUse.name, status: 'done' }]);
      message.warning(error?.message || 'License selected. Enter details manually.');
    }
    return false;
  };

  const handleSubmit = async (values: any) => {
    if (!cidFile) {
      message.error('Please upload CID (Citizen Identity Card)');
      return;
    }
    if (!licenseFile) {
      message.error('Please upload Company License Document');
      return;
    }

    setLoading(true);
    try {
      const companyData: CompanyCreate = {
        name: values.name,
        description: values.description,
        email: values.email,
        phone: values.phone,
        address: values.address,
        registration_number: values.registration_number,
        tax_id: values.tax_id,
        plan_type: values.plan_type || 'basic',
        max_users: values.max_users || 10,
        max_locations: values.max_locations || 3,
        subscription_period: values.subscription_period || 'yearly',
        license_type: values.license_type,
        admin_username: values.admin_username,
        admin_email: values.admin_email,
        admin_password: values.admin_password,
        admin_name: values.admin_name,
        primary_contact: values.primary_contact,
        cid_file: cidFile,
        license_file: licenseFile || undefined,
      };

      await superAdminService.createCompanyWithAdminAndCID(companyData);
      message.success('Company created successfully!');
      onSuccess();
    } catch (error: any) {
      message.error(error.message || 'Failed to create company');
      console.error('Error creating company:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <ApartmentOutlined />
          <span>Create Company</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      {licenseData && (
        <Alert
          message="License data loaded"
          description="Company information has been pre-filled from the uploaded license."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          plan_type: 'basic',
          max_users: 2,
          max_locations: 1,
          subscription_period: 'yearly',
        }}
        autoComplete="off"
      >
        <div style={{ position: 'absolute', left: '-9999px', top: 0, width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
          <input type="text" name="username" tabIndex={-1} autoComplete="username" readOnly aria-hidden="true" />
          <input type="password" name="password" tabIndex={-1} autoComplete="current-password" readOnly aria-hidden="true" />
          <input type="password" name="new-password" tabIndex={-1} autoComplete="new-password" readOnly aria-hidden="true" />
        </div>
        <Form.Item
          label="Company License Document"
          required
          tooltip="Upload the company license document to automatically extract company name, registration number, and license type"
        >
          <Upload
            beforeUpload={handleLicenseUpload}
            fileList={licenseFileList}
            onRemove={() => {
              setLicenseFile(null);
              setLicenseFileList([]);
            }}
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
          >
            <Button icon={<UploadOutlined />}>
              Upload License (PDF, JPG, PNG — over 2MB is compressed)
            </Button>
          </Upload>
          <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
            Upload license to auto-fill: Company Name, Registration Number, and License Type
          </div>
        </Form.Item>

        {/* Auto-filled fields from license - Top Priority */}
        <Form.Item
          label="Company Name"
          name="name"
          rules={[{ required: true, message: 'Please enter company name' }]}
        >
          <Input autoComplete="off" placeholder="Enter company name (will be auto-filled from license)" />
        </Form.Item>

        <Form.Item
          label="Registration Number"
          name="registration_number"
        >
          <Input autoComplete="off" placeholder="Enter registration number (will be auto-filled from license)" />
        </Form.Item>

        <Form.Item
          label="License Type"
          name="license_type"
          tooltip="Select whether this is a micro or macro license (will be auto-filled from license)"
        >
          <Select placeholder="Select license type (will be auto-filled from license)">
            <Option value="micro">Micro</Option>
            <Option value="macro">Macro</Option>
          </Select>
        </Form.Item>

        {/* Other Company Information */}

        <Form.Item
          label="Description"
          name="description"
        >
          <TextArea autoComplete="off" rows={2} placeholder="Enter company description" />
        </Form.Item>

        <Form.Item
          label="Email"
          name="email"
          rules={[{ type: 'email', message: 'Please enter a valid email' }]}
        >
          <Input autoComplete="off" placeholder="Enter company email" />
        </Form.Item>

        <Form.Item
          label="Phone"
          name="phone"
        >
          <Input autoComplete="off" placeholder="Enter company phone" />
        </Form.Item>

        <Form.Item
          label="Address"
          name="address"
        >
          <TextArea autoComplete="off" rows={2} placeholder="Enter company address" />
        </Form.Item>

        <Form.Item
          label="Tax ID"
          name="tax_id"
        >
          <Input autoComplete="off" placeholder="Enter tax ID" />
        </Form.Item>

        <Form.Item
          label="Plan Type"
          name="plan_type"
        >
          <Select
            onChange={(v) => {
              const plan = (v || 'basic').toString().toLowerCase();
              const presets: Record<string, { max_users: number; max_locations: number }> = {
                basic: { max_users: 2, max_locations: 1 }, // shown as Standard
                standard: { max_users: 2, max_locations: 1 },
                premium: { max_users: 6, max_locations: 4 },
                enterprise: { max_users: 15, max_locations: 6 },
              };
              const p = presets[plan] || presets.basic;
              form.setFieldsValue({ max_users: p.max_users, max_locations: p.max_locations });
            }}
          >
            <Option value="basic">Standard</Option>
            <Option value="premium">Premium</Option>
            <Option value="enterprise">Enterprise</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Max Users"
          name="max_users"
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="Max Locations"
          name="max_locations"
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="Subscription Period"
          name="subscription_period"
          rules={[{ required: true, message: 'Please select subscription period' }]}
        >
          <Select placeholder="Select subscription period">
            <Option value="monthly">Monthly</Option>
            <Option value="yearly">Yearly</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Admin Username"
          name="admin_username"
          rules={[{ required: true, message: 'Please enter admin username' }]}
        >
          <Input
            autoComplete="off"
            placeholder="Enter admin username"
            name="company_admin_username_field"
            readOnly
            onFocus={(e) => e.currentTarget.removeAttribute('readonly')}
            data-lpignore="true"
            data-form-type="other"
          />
        </Form.Item>

        <Form.Item
          label="Admin Email"
          name="admin_email"
          rules={[
            { required: true, message: 'Please enter admin email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input
            autoComplete="off"
            placeholder="Enter admin email"
            name="company_admin_email_field"
            readOnly
            onFocus={(e) => e.currentTarget.removeAttribute('readonly')}
            data-lpignore="true"
            data-form-type="other"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
        </Form.Item>

        <Form.Item
          label="Admin Password"
          name="admin_password"
          rules={[
            { required: true, message: 'Please enter admin password' },
            { min: 6, message: 'Password must be at least 6 characters' },
          ]}
        >
          <Input.Password
            placeholder="Enter admin password"
            autoComplete="one-time-code"
            name="company_admin_password_field"
            readOnly
            onFocus={(e) => e.currentTarget.removeAttribute('readonly')}
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            data-form-type="other"
          />
        </Form.Item>

        <Form.Item
          label="CID (Citizen Identity Card)"
          required
          rules={[{ required: true, message: 'Please upload CID' }]}
        >
          <Upload
            beforeUpload={handleCidUpload}
            fileList={cidFileList}
            onRemove={() => {
              setCidFile(null);
              setCidFileList([]);
            }}
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
          >
            <Button icon={<IdcardOutlined />}>
              Upload CID (PDF, JPG, PNG — over 2MB is compressed)
            </Button>
          </Upload>
          <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
            Required: Upload the owner's Citizen Identity Card. Admin name will be auto-filled from CID.
          </div>
        </Form.Item>

        <Form.Item
          label="Admin Name"
          name="admin_name"
          rules={[{ required: true, message: 'Please enter admin name' }]}
        >
          <Input autoComplete="off" placeholder="Enter admin full name (will be auto-filled from CID)" />
        </Form.Item>

        <Form.Item
          label="Primary Contact"
          name="primary_contact"
          rules={[{ required: true, message: 'Please enter primary contact' }]}
        >
          <Input autoComplete="off" placeholder="+975 XXXXXXXX" />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Create Company
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateCompanyModal;
